import React, { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { ContentRoute, LayoutSplashScreen } from "../_metronic/layout";
import RoutePaths from "./constants/routepaths";
import { useSelector } from "react-redux";
import { ModulesName, userHasModule } from "./General/roleModuleActions";
import { resolveDefaultLandingRoute } from "./General/landingRoute";
import { ModuleRoute } from "./General/ModuleRoute";
import StubModeBanner from "./components/StubModeBanner";
import { useFeatureFlagsLoader, useFeatureFlagsLoaded } from "./_redux/featureFlags/useFeatureFlag";

// All page components are lazy-loaded so webpack splits them into separate chunks.
// The top-level Suspense boundary (app.js) and the one below both show the splash screen.
const DashboardPage        = lazy(() => import("./pages/dashboardpage").then(m => ({ default: m.DashboardPage })));
const BuilderPage          = lazy(() => import("./pages/builderpage").then(m => ({ default: m.BuilderPage })));
const MyPage               = lazy(() => import("./pages/mypage").then(m => ({ default: m.MyPage })));
const Resident             = lazy(() => import("./pages//resident/resident"));
const Facility             = lazy(() => import("./pages/facility/facility"));
const FacilityQueue        = lazy(() => import("./pages/facilityQueue/facilityQueue"));
const WorkQueueWoundOrders = lazy(() => import("./pages/workqueue/workQueueWoundOrders"));
const BillingQueueOrders   = lazy(() => import("./pages/billing/billingQueueOrders"));
const Physician            = lazy(() => import("./pages/physician/physicianList"));
const SetupNav             = lazy(() => import("./pages/Setups/setupNav"));
const ReportNav            = lazy(() => import("./pages/reports/reportsNav"));
const AssessmentReportNav  = lazy(() => import("./pages/reports/assessmentReportsNav"));
const PostVisitEmail       = lazy(() => import("./pages/postVisitEmail/postVisitEmail"));
const EHRVendors           = lazy(() => import("./pages/ehrvendors/ehrvendors"));
const SetupBrightree       = lazy(() => import("./pages/Brightree/SetupBrightree"));
const ProductNavPage       = lazy(() => import("./pages/products/productnav"));
const RepFirmNavPage       = lazy(() => import("./pages/repfirm/repfirmnav"));
const TabletDataListPage   = lazy(() => import("./pages/tabletdata/tabletlist"));
const OrdersPage           = lazy(() => import("./pages/orders/orderspage"));
const CmnQueueMain         = lazy(() => import("./pages/cmnQueue/cmnQueueMain"));
const ModeSelect           = lazy(() => import("./pages/modeSelect/modeSelect"));
const FeatureSwitchesAdmin = lazy(() => import("./pages/admin/featureSwitches/featureSwitchesAdmin"));
const ErrorCatalogAdmin    = lazy(() => import("./pages/admin/errorCatalog/errorCatalogAdmin"));
const BrightreeMappingAdmin = lazy(() => import("./pages/admin/brightreeMapping/brightreeMappingAdmin"));
const APP_MODE_KEY = "fc_app_mode";
const GoogleMaterialPage   = lazy(() => import("./modules/GoogleMaterialExamples/GoogleMaterialPage"));
const ReactBootstrapPage   = lazy(() => import("./modules/ReactBootstrapExamples/ReactBootstrapPage"));
const ECommercePage        = lazy(() => import("./modules/ECommerce/pages/eCommercePage"));

export default function BasePage() {
  const { user } = useSelector((state) => state.auth);
  useFeatureFlagsLoader();
  const featureFlagsLoaded = useFeatureFlagsLoaded();

  if (!featureFlagsLoaded) {
    return <LayoutSplashScreen />;
  }

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <Switch>
        <Route exact path="/" render={() => {
          // Don't land on the Mode Select ("stub") page — send mode-capable users
          // straight to the Facility Queue; everyone else to their role-based default.
          const canPickMode = userHasModule(user, ModulesName.Module_ModeSelect);
          const target = (canPickMode && !sessionStorage.getItem(APP_MODE_KEY))
            ? RoutePaths.Page_FacilityQueue
            : resolveDefaultLandingRoute(user);
          return <Redirect exact to={target} />;
        }} />
        <ContentRoute path={RoutePaths.Page_ModeSelect}        component={ModeSelect} />
        <ContentRoute path={`${RoutePaths.Page_FeatureSwitchesAdmin}/:userId?`} component={FeatureSwitchesAdmin} />
        <ContentRoute path={RoutePaths.Page_ErrorCatalogAdmin} component={ErrorCatalogAdmin} />
        <ContentRoute path={RoutePaths.Page_BrightreeMappingAdmin} component={BrightreeMappingAdmin} />
        <ContentRoute path={RoutePaths.Page_Dashboard}          component={DashboardPage} />
        <ContentRoute path={RoutePaths.Page_Builder}            component={BuilderPage} />
        <ContentRoute path={RoutePaths.Page_MyPage}             component={MyPage} />
        <ContentRoute path={RoutePaths.Page_WorkQueue}          component={WorkQueueWoundOrders} />
        <ContentRoute path={RoutePaths.Page_Billing}            component={BillingQueueOrders} />
        <ContentRoute path={RoutePaths.Page_Facility}           component={Facility} />
        <ModuleRoute path={RoutePaths.Page_FacilityQueue}       module={ModulesName.Module_FacilityQueue} component={FacilityQueue} />
        <ModuleRoute path={RoutePaths.Page_CmnQueue}            module={ModulesName.Module_CmnQueue} component={CmnQueueMain} />
        <ContentRoute path={RoutePaths.Page_Resident}           component={Resident} />
        <ContentRoute path={RoutePaths.Page_Physician}          component={Physician} />
        <ContentRoute path={RoutePaths.Page_ProductsNav}        component={ProductNavPage} />
        <ContentRoute path={RoutePaths.Page_RepFirmNav}         component={RepFirmNavPage} />
        <ContentRoute path={RoutePaths.Page_TabletData}         component={TabletDataListPage} />
        <ContentRoute path={RoutePaths.Page_EHRVendors}         component={EHRVendors} />
        <ContentRoute path={RoutePaths.Page_SetupNav}           component={SetupNav} />
        <ContentRoute path={RoutePaths.Page_ReportNav}          component={ReportNav} />
        <ContentRoute path={RoutePaths.Page_PostVisitEmail}     component={PostVisitEmail} />
        <ContentRoute path={RoutePaths.Page_AssessmentReportNav} component={AssessmentReportNav} />
        <ContentRoute path={RoutePaths.Page_SetupBrightree}     component={SetupBrightree} />
        <Route        path={RoutePaths.Page_OrderNav}           component={OrdersPage} />
        <Route        path="/google-material"                   component={GoogleMaterialPage} />
        <Route        path="/react-bootstrap"                   component={ReactBootstrapPage} />
        <Route        path="/e-commerce"                        component={ECommercePage} />
        <Redirect to="error/error-v1" />
      </Switch>
    </Suspense>
  );
}
